import { NgIf, CommonModule } from '@angular/common'
import { Component, inject, Output, EventEmitter, OnInit } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Store } from '@ngrx/store'
import {
  ExtendedSalaryComponent,
  StatutoryCompliances
} from '../../../store/employee-salary/employee-salary.state'
import { combineLatest, take, tap } from 'rxjs'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import {
  PayoutAgreement,
  PayoutFrequency
} from '../../../store/payout-agreement/payout-agreement.state'
import {
  selectPayout,
  selectRevisedPayout
} from '../../../store/payout-agreement/payout-agreement.reducers'

export interface SalaryComparisonItem {
  itemName: string
  category: 'CTC' | 'Salary Component' | 'Other Component' | 'Statutory Compliance'
  previousValue: number | string | Date | null
  revisedValue: number | string | Date | null
  difference?: number
  isChanged: boolean
  percentageChange?: number
  isNumeric: boolean
  isComplianceList: boolean
  addedCompliances?: string[]
  removedCompliances?: string[]
}

@Component({
  selector: 'app-revised-payout-summary',
  imports: [NgIf, FormsModule, ReactiveFormsModule, CommonModule, CapitalizePipe],
  templateUrl: './revised-payout-summary.component.html',
  styleUrl: './revised-payout-summary.component.scss'
})
export class RevisedPayoutSummaryComponent implements OnInit {
  private store = inject(Store)
  @Output() saveButtonState = new EventEmitter<boolean>()

  previousPayout: PayoutAgreement | null = null
  revisedPayout: PayoutAgreement | null = null
  payoutComparison: SalaryComparisonItem[] = []

  async ngOnInit() {
    combineLatest([this.store.select(selectPayout), this.store.select(selectRevisedPayout)])
      .pipe(
        take(1),
        tap(([previousPayout, revisedPayout]) => {
          if (previousPayout && revisedPayout) {
            this.previousPayout = previousPayout
            this.revisedPayout = revisedPayout
            this.payoutComparison = this.generateSalaryComparison(previousPayout, revisedPayout)
          } else {
            this.previousPayout = null
            this.revisedPayout = null
            this.payoutComparison = []
          }
          this.saveButtonState.emit(!(this.payoutComparison.length > 0))
        })
      )
      .subscribe()
  }

  generateSalaryComparison(prev: PayoutAgreement, rev: PayoutAgreement): SalaryComparisonItem[] {
    const comparison: SalaryComparisonItem[] = []
    if (!prev.payoutData || !rev.payoutData || prev.ctc === null || rev.ctc === null) {
      return []
    }

    // 0. Compare CTC level Details
    this.compareDates(comparison, 'Effective From', prev.effectiveFrom, rev.effectiveFrom, 'CTC')
    this.compareDates(comparison, 'Payout From', prev.payoutFrom, rev.payoutFrom, 'CTC')
    this.compareStrings(
      comparison,
      'Payout Frequency',
      prev.payoutFrequency,
      rev.payoutFrequency,
      'CTC'
    )

    // 1. Compare CTC Amount
    this.compareValues(comparison, 'CTC Amount', prev.ctc, rev.ctc, 'CTC')

    // 2. Compare Fixed Payout Data Components
    this.compareValues(
      comparison,
      'Basic',
      prev.payoutData.basic,
      rev.payoutData.basic,
      'Salary Component'
    )
    //if (this.user.role !== 'Contractor') {
    //  this.compareValues(
    //    comparison,
    //    'HRA',
    //    prev.salaryData.hra,
    //    rev.salaryData.hra,
    //    'Salary Component'
    //  )
    //}

    const wasMonthly = prev.payoutFrequency === PayoutFrequency.MONTHLY
    const isMonthly = rev.payoutFrequency === PayoutFrequency.MONTHLY

    if (wasMonthly || isMonthly) {
      if (wasMonthly && isMonthly) {
        this.compareValues(
          comparison,
          'PF',
          prev.payoutData.pf,
          rev.payoutData.pf,
          'Salary Component'
        )
      } else if (wasMonthly && !isMonthly) {
        // PF was removed because frequency changed from monthly
        comparison.push({
          itemName: 'PF',
          category: 'Salary Component',
          previousValue: prev.payoutData.pf,
          revisedValue: 'N/A (Frequency changed)',
          isNumeric: false,
          isComplianceList: false,
          isChanged: true
        })
      } else if (!wasMonthly && isMonthly) {
        // PF was added because frequency changed to monthly
        comparison.push({
          itemName: 'PF',
          category: 'Salary Component',
          previousValue: 'N/A (Frequency was not monthly)',
          revisedValue: rev.payoutData.pf,
          isNumeric: false,
          isComplianceList: false,
          isChanged: true
        })
      }
    }
    this.compareValues(
      comparison,
      'Special Allowance',
      prev.payoutData.specialAllowance,
      rev.payoutData.specialAllowance,
      'Salary Component'
    )

    // 3. Compare Other Components (Dynamic)
    this.compareOtherComponents(
      comparison,
      prev.payoutData.otherComponents,
      rev.payoutData.otherComponents
    )

    // 4. Compare Statutory Compliances (as lists)
    if (prev.statutoryCompliances && rev.statutoryCompliances) {
      this.compareStatutoryCompliances(
        comparison,
        prev.statutoryCompliances,
        rev.statutoryCompliances
      )
    }

    return comparison.filter(item => item.isChanged === true)
    //return comparison
  }

  compareStrings(
    comparison: SalaryComparisonItem[],
    itemName: string,
    previousValue: string | null | undefined,
    revisedValue: string | null | undefined,
    category: SalaryComparisonItem['category']
  ): void {
    const prev = previousValue ?? 'Not Set'
    const rev = revisedValue ?? 'Not Set'

    if (prev === rev) return

    comparison.push({
      itemName,
      category,
      previousValue: prev,
      revisedValue: rev,
      isNumeric: false,
      isComplianceList: false,
      isChanged: true
    })
  }

  // Helper to compare non-numeric/date values
  compareDates(
    comparison: SalaryComparisonItem[],
    itemName: string,
    previousValue: string | Date | null | undefined,
    revisedValue: string | Date | null | undefined,
    category: SalaryComparisonItem['category']
  ): void {
    // Determine display/comparison values consistently
    const formatForComparison = (val: string | Date | null | undefined): string => {
      if (val instanceof Date) return val.toISOString()
      if (val === null || val === undefined) return 'null'
      return String(val)
    }

    const prevComp = formatForComparison(previousValue)
    const revComp = formatForComparison(revisedValue)

    if (prevComp === revComp) return // No change based on consistent comparison

    // Prepare values for storage in the item (keep original type if possible)
    const prevStoredValue = previousValue ?? null
    const revStoredValue = revisedValue ?? null

    const item: SalaryComparisonItem = {
      itemName,
      category,
      // Store original Date object, string, number, or null
      previousValue: prevStoredValue,
      revisedValue: revStoredValue,
      isNumeric: false,
      isComplianceList: false,
      isChanged: true
    }
    comparison.push(item)
  }

  // Helper to compare static salary items
  compareValues(
    comparison: SalaryComparisonItem[],
    itemName: string,
    previousValue: number,
    revisedValue: number,
    category: SalaryComparisonItem['category']
  ): void {
    if (previousValue === revisedValue) return

    const item: Partial<SalaryComparisonItem> = {
      itemName,
      category,
      previousValue,
      revisedValue,
      isNumeric: true,
      isComplianceList: false
    }

    item.difference = revisedValue - previousValue
    item.isChanged = item.difference !== 0 ? true : false
    if (previousValue !== 0) {
      item.percentageChange = parseFloat(((item.difference / previousValue) * 100).toFixed(2))
    } else if (revisedValue !== 0) {
      item.percentageChange = 100
    } else {
      item.percentageChange = 0
    }

    comparison.push(item as SalaryComparisonItem)
  }

  // Helper to compare the dynamic 'otherComponents' (dynamic salary components) array
  compareOtherComponents(
    comparison: SalaryComparisonItem[],
    prevComponents: ExtendedSalaryComponent[],
    revComponents: ExtendedSalaryComponent[]
  ): void {
    const prevMap = new Map(prevComponents.map(c => [c.id, c]))
    const revMap = new Map(revComponents.map(c => [c.id, c]))

    // Check existing/modified/removed components
    prevMap.forEach((prevComp, id) => {
      const revComp = revMap.get(id)
      const itemNameBase = prevComp.name

      if (revComp) {
        // Component exists in both, compare amounts
        if (prevComp.componentType === 'customDeductions') {
          this.compareValues(
            comparison,
            `${itemNameBase} (Employee Contribution)`,
            prevComp.employeeContribution ?? 0,
            revComp.employeeContribution ?? 0,
            'Other Component'
          )
        }
        this.compareValues(
          comparison,
          `${itemNameBase} (Employer Contribution)`,
          prevComp.employerContribution ?? 0,
          revComp.employerContribution ?? 0,
          'Other Component'
        )
      } else {
        // Component was removed
        if (prevComp.componentType === 'customDeductions') {
          comparison.push({
            itemName: `${itemNameBase} (Employee Contribution)`,
            category: 'Other Component',
            previousValue: prevComp.employeeContribution ?? 0,
            revisedValue: 'Removed',
            isNumeric: false,
            isComplianceList: false,
            isChanged: false
          })
        }
        comparison.push({
          itemName: `${itemNameBase} (Employer Contribution)`,
          category: 'Other Component',
          previousValue: prevComp.employerContribution ?? 0,
          revisedValue: 'Removed',
          isNumeric: false,
          isComplianceList: false,
          isChanged: false
        })
      }
    })

    // Check for added components
    revMap.forEach((revComp, id) => {
      if (!prevMap.has(id)) {
        // Component was added
        const itemNameBase = revComp.name
        // Add entries for added contributions if they exist
        if (revComp.componentType === 'customDeductions') {
          comparison.push({
            itemName: `${itemNameBase} (Employee Contribution)`,
            category: 'Other Component',
            previousValue: 'N/A',
            revisedValue: revComp.employeeContribution ?? 0,
            isNumeric: false,
            isComplianceList: false,
            isChanged: true,
            difference: 100
          })
        }
        comparison.push({
          itemName: `${itemNameBase} (Employer Contribution)`,
          category: 'Other Component',
          previousValue: 'N/A',
          revisedValue: revComp.employerContribution ?? 0,
          isNumeric: false,
          isComplianceList: false,
          isChanged: true,
          difference: 100
        })
      }
    })
  }

  // Helper to get enabled statutory compliance keys as user-friendly strings
  getEnabledCompliances(compliances: StatutoryCompliances): string[] {
    const enabled: string[] = []
    if (compliances.pf) enabled.push('PF')
    if (compliances.eps) enabled.push('EPS')
    if (compliances.employerCapping) enabled.push('PF Employer Capping')
    if (compliances.esi) enabled.push('ESI')
    if (compliances.lwf) enabled.push('LWF')
    if (compliances.pt) enabled.push('PT')
    return enabled.sort()
  }

  // Helper to compare statutory compliances
  compareStatutoryCompliances(
    comparison: SalaryComparisonItem[],
    prev: StatutoryCompliances,
    rev: StatutoryCompliances
  ): void {
    const prevEnabled = this.getEnabledCompliances(prev)
    const revEnabled = this.getEnabledCompliances(rev)
    const prevSet = new Set(prevEnabled)
    const revSet = new Set(revEnabled)

    // Find items in revEnabled but not in prevEnabled (Added)
    const added = revEnabled.filter(item => !prevSet.has(item))

    // Find items in prevEnabled but not in revEnabled (Removed)
    const removed = prevEnabled.filter(item => !revSet.has(item))

    const isChanged = added.length > 0 || removed.length > 0

    // Only add the row if there's an actual change in compliances
    if (isChanged) {
      comparison.push({
        itemName: 'Statutory Compliances',
        category: 'Statutory Compliance',
        previousValue: prevEnabled.length > 0 ? prevEnabled.join(', ') : 'None',
        revisedValue: revEnabled.length > 0 ? revEnabled.join(', ') : 'None',
        isNumeric: false,
        isComplianceList: true,
        isChanged: true,
        addedCompliances: added,
        removedCompliances: removed
        // difference and percentageChange are not applicable/calculated here
      })
    } // Optional: If you always want to show the compliance row even if unchanged
    /* else {
         comparison.push({
           itemName: 'Statutory Compliances',
           category: 'Statutory Compliance',
           previousValue: prevEnabled.length > 0 ? prevEnabled.join(', ') : 'None',
           revisedValue: revEnabled.length > 0 ? revEnabled.join(', ') : 'None',
           isNumeric: false,
           isComplianceList: true,
           isChanged: false,
           addedCompliances: [],
           removedCompliances: []
         });
     } */
  }
}
